(() => {
  'use strict';

  const selector = 'cart-drawer.mk-cart';
  const namespace = 'MadjaKajaCart';

  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const controllers = new Map();

  class CartPresentation {
    constructor(drawer) {
      this.drawer = drawer;
      this.frame = 0;
      this.timeout = 0;
      this.expiresAt = 0;
      this.timer = drawer.querySelector('[data-mk-cart-timer]');
      const duration = Number(this.timer?.closest('.mk-cart-session')?.dataset.duration || 10);
      this.duration = Math.max(1, Math.round((Number.isFinite(duration) && duration > 0 ? duration : 10) * 60)) * 1000;
      this.storageKey = `mk-cart-session:${window.Shopify?.theme?.id || 'default'}`;
      this.onChange = this.scheduleSync.bind(this);
      this.onVisibility = () => {
        if (!document.hidden) this.scheduleSync();
      };

      document.addEventListener('theme:cart:change', this.onChange);
      document.addEventListener('theme:cart-drawer:open', this.onChange);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.sync();
    }

    scheduleSync() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.sync();
      });
    }

    sync() {
      if (!this.drawer.isConnected) {
        this.destroy();
        controllers.delete(this.drawer);
        return;
      }

      const source = this.drawer.querySelector('[data-cart-total]');
      const total = this.drawer.querySelector('[data-mk-cart-total]');
      if (source && total) total.textContent = source.dataset.mkCartTotalFormatted || source.textContent.trim();

      const discountsSource = this.drawer.querySelector('[data-mk-cart-discounts-source]');
      const discounts = this.drawer.querySelector('[data-mk-cart-discounts]');
      if (discountsSource && discounts) {
        const markup = discountsSource.innerHTML.trim();
        if (discounts.innerHTML.trim() !== markup) discounts.innerHTML = markup;
        discounts.hidden = !markup;
      }

      if (!this.timer) return;

      const hasItems = Boolean(this.drawer.querySelector('[data-items-holder] [data-item]'));
      if (!hasItems) {
        clearTimeout(this.timeout);
        this.timeout = 0;
        this.expiresAt = 0;
        this.writeSession(null);
        this.renderTime(this.duration - 1000);
        return;
      }

      if (!this.expiresAt) {
        const stored = this.readSession();
        this.expiresAt = stored || Date.now() + this.duration - 1000;
        this.writeSession(this.expiresAt);
      }

      this.tick();
    }

    readSession() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (stored?.duration === this.duration && Number.isFinite(stored.expiresAt) && stored.expiresAt > 0 && stored.expiresAt <= Date.now() + this.duration - 1000) return stored.expiresAt;
      } catch (_) {
      }
      return 0;
    }

    writeSession(value) {
      try {
        if (value === null) sessionStorage.removeItem(this.storageKey);
        else sessionStorage.setItem(this.storageKey, JSON.stringify({ expiresAt: value, duration: this.duration }));
      } catch (_) {
      }
    }

    renderTime(milliseconds) {
      const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
      const minutes = Math.floor(seconds / 60);
      this.timer.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
      this.timer.closest('.mk-cart-session')?.classList.toggle('is-expired', seconds === 0);
    }

    tick() {
      clearTimeout(this.timeout);
      const now = Date.now();
      if (now >= this.expiresAt + 1000) {
        this.expiresAt += (Math.floor((now - this.expiresAt - 1000) / this.duration) + 1) * this.duration;
        this.writeSession(this.expiresAt);
      }
      const remaining = Math.max(0, this.expiresAt - now);
      this.renderTime(remaining);
      this.timeout = setTimeout(() => this.tick(), remaining > 0 ? remaining % 1000 || 1000 : this.expiresAt + 1000 - now);
    }

    destroy() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timeout);
      document.removeEventListener('theme:cart:change', this.onChange);
      document.removeEventListener('theme:cart-drawer:open', this.onChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  const init = (scope) => {
    const drawers = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) drawers.unshift(scope);
    drawers.forEach((drawer) => {
      if (!controllers.has(drawer)) controllers.set(drawer, new CartPresentation(drawer));
    });
  };

  const unload = (event) => {
    controllers.forEach((controller, drawer) => {
      if (event.target === drawer || event.target.contains(drawer)) {
        controller.destroy();
        controllers.delete(drawer);
      }
    });
  };

  window[namespace] = { init };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', unload);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
